import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import { useAuthToken } from '../features/auth/useAuthToken';

export interface UserProfileResponse {
  id: string;
  email: string;
  name: string | null;
  organization_id: string | null;
  role: string | null;
  user_uid: string;
  attribute_collection_completed: boolean | null;
  attribute_user_type_answered: boolean | null;
  attribute_gender_answered: boolean | null;
  attribute_occupation_answered: boolean | null;
  attribute_usage_purpose_answered: boolean | null;
  attribute_discovery_channel_answered: boolean | null;
  attribute_usage_purpose: string[] | null;
  attribute_discovery_channel: string[] | null;
  attribute_collection_dismissed: boolean;
  show_help_tooltips: boolean;
  navigation_guide_completed_at: string | null;
  created_at: string;
  updated_at: string | null;
}

/**
 * APIからユーザープロファイルを取得するフック
 */
export function useUserProfile() {
  const { token } = useAuthToken();

  return useQuery<UserProfileResponse>({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const response = await apiClient.get('/users/me');
      return response.data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });
}

export interface SurveyOption {
  value: string;
  label?: string;
}
export interface SurveyQuestion {
  key: string;
  label: string;
  options: SurveyOption[];
  /** 複数選択設問 (回答は値コードの配列で送る) */
  multi?: boolean;
}
export interface SurveyConfigResponse {
  partner_code: string;
  credits_per_answer: number;
  max_credits_per_org: number;
  questions: SurveyQuestion[];
}

/**
 * パートナー別の初回アンケート構成 (設問・選択肢・付与額) を取得するフック。
 * 属性収集モーダルが未完了のときだけ引く。
 */
export function useSurveyConfig(enabled: boolean) {
  const { token } = useAuthToken();
  return useQuery<SurveyConfigResponse>({
    queryKey: ['surveyConfig'],
    queryFn: async () => {
      const response = await apiClient.get('/users/me/survey');
      return response.data;
    },
    enabled: !!token && enabled,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * ユーザー属性の1項目を送信するミューテーション
 */
export function useSubmitUserAttribute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { question: string; answer: string | string[] }) => {
      const response = await apiClient.post('/users/me/attributes', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      queryClient.invalidateQueries({ queryKey: ['credits'] });
    },
  });
}

/**
 * 属性収集をスキップするミューテーション
 */
export function useDismissAttributeCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post('/users/me/attributes/dismiss');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    },
  });
}
